import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Star, TrendingUp } from "lucide-react";
import { overallRating } from "../mock";

export const ReviewRating = () => {
  const getPercentage = (score, max) => (score / max) * 100;

  return (
    <section id="reviews-rating" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-200">
            <Star className="h-4 w-4 mr-2 fill-teal-700" />
            Verified Reviews
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Family Reviews & Ratings
          </h2>
          <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real feedback from families who trust us with their loved ones' care
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Overall Rating Card */}
            <Card className="lg:col-span-1 border-2 border-teal-200 shadow-xl">
              <CardContent className="pt-8 text-center">
                <div className="mb-4">
                  <div className="text-7xl font-bold text-teal-700 mb-2">
                    {overallRating.score}
                  </div>
                  <div className="text-2xl text-gray-600 mb-4">out of {overallRating.maxScore}</div>
                  <div className="flex justify-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-6 w-6 ${
                          i < Math.floor(overallRating.score / 2)
                            ? "fill-orange-500 text-orange-500"
                            : "text-gray-300"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600">
                    Based on <span className="font-bold text-teal-700">{overallRating.totalReviews} verified</span> family reviews
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Latest review: {overallRating.lastReviewDate}
                  </p>
                </div>

                <div className="bg-teal-50 rounded-lg p-4 mt-6">
                  <div className="flex items-center justify-center gap-2 text-teal-700">
                    <TrendingUp className="h-5 w-5" />
                    <span className="font-semibold">Excellent Rating</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rating Breakdown */}
            <Card className="lg:col-span-2 border-none shadow-xl">
              <CardContent className="pt-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Rating Breakdown</h3>
                <div className="space-y-4">
                  {overallRating.breakdown.map((item, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-700">{item.category}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">({item.reviews} reviews)</span>
                          <span className="font-bold text-teal-700 min-w-[3rem] text-right">
                            {item.score}/10
                          </span>
                        </div>
                      </div>
                      <div className="relative">
                        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-teal-500 to-teal-600 rounded-full transition-all duration-500"
                            style={{ width: `${getPercentage(item.score, 10)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-4 bg-orange-50 rounded-lg border-l-4 border-l-orange-500">
                  <p className="text-sm text-gray-700">
                    <span className="font-bold">All reviews are verified.</span> We only display feedback from family members and representatives of current or recent residents.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
